import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { Building2, ImageIcon, Receipt, Mail, Bell, User, Share2, FileCheck2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSettingsPermissions } from './hooks/useSettingsPermissions'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

interface SettingsSection {
  path: string
  label: string
  icon: React.ElementType
  scope: 'org' | 'user'
  badge?: string
}

const SECTIONS: SettingsSection[] = [
  { path: 'general', label: 'General', icon: Building2, scope: 'org' },
  { path: 'marca', label: 'Marca', icon: ImageIcon, scope: 'org' },
  { path: 'fiscal', label: 'Fiscal', icon: Receipt, scope: 'org' },
  { path: 'facturacion', label: 'Facturación', icon: FileCheck2, scope: 'org' },
  { path: 'comunicaciones', label: 'Comunicaciones', icon: Mail, scope: 'org' },
  { path: 'redes', label: 'Redes', icon: Share2, scope: 'org' },
  { path: 'notificaciones', label: 'Notificaciones', icon: Bell, scope: 'user' },
  { path: 'cuenta', label: 'Mi Cuenta', icon: User, scope: 'user' },
]

export function SettingsSidebar() {
  const { canViewOrgSettings } = useSettingsPermissions()
  const navigate = useNavigate()
  const location = useLocation()

  const visibleSections = SECTIONS.filter(
    (s) => s.scope === 'user' || canViewOrgSettings
  )

  const orgSections = visibleSections.filter((s) => s.scope === 'org')
  const userSections = visibleSections.filter((s) => s.scope === 'user')

  // Current active path for mobile select
  const currentPath = location.pathname.split('/').pop() ?? 'general'

  return (
    <>
      {/* Desktop sidebar */}
      <nav className="hidden md:flex w-[220px] flex-col border-r bg-muted/30 p-4 gap-1 overflow-y-auto shrink-0">
        {orgSections.length > 0 && (
          <>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 mb-1">
              Organización
            </p>
            {orgSections.map((section) => (
              <SidebarItem key={section.path} section={section} />
            ))}
            <div className="my-2 border-b" />
          </>
        )}
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 mb-1">
          Personal
        </p>
        {userSections.map((section) => (
          <SidebarItem key={section.path} section={section} />
        ))}
      </nav>

      {/* Mobile dropdown */}
      <div className="md:hidden px-4 pt-4 pb-2">
        <Select value={currentPath} onValueChange={(val) => navigate(`/configuracion/${val}`)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {orgSections.length > 0 && (
              <>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1">
                  Organización
                </p>
                {orgSections.map((s) => (
                  <SelectItem key={s.path} value={s.path}>
                    {s.label} {s.badge && `(${s.badge})`}
                  </SelectItem>
                ))}
              </>
            )}
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1 mt-1">
              Personal
            </p>
            {userSections.map((s) => (
              <SelectItem key={s.path} value={s.path}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

function SidebarItem({ section }: { section: SettingsSection }) {
  const Icon = section.icon

  return (
    <NavLink
      to={`/configuracion/${section.path}`}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{section.label}</span>
      {section.badge && (
        <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
          {section.badge}
        </Badge>
      )}
    </NavLink>
  )
}
